import { getTouchedElemsFromFirstArray } from '@/shared/lib/getTouchedElemsFromFirstArray';
import { getUniqueElemsFromFirstArray } from '@/shared/lib/getUniqueElemsFromFirstArray';
import { tryCatch } from '@/shared/lib/tryCatch';
import { uploadImg } from '@/shared/lib/uploadImg';
import { RootState } from '@/shared/model';
import { setNotification } from '@/shared/model/appSlice';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { api } from '../api';
import { IApiRule, IFoods, IFoodsFilter, IRule } from '../types';

export const fetchAll = createAsyncThunk(
  'fetchAll',
  async (
    { filters }: { filters?: IFoodsFilter },
    { rejectWithValue, dispatch }
  ) => {
    const [data, err] = await tryCatch<{
      foods: IFoods[];
    }>(api.getAll({ data: filters }));
    if (err) {
      dispatch(
        setNotification({
          value: 'Ошибка при получении продуктов',
          variant: 'error',
        })
      );
      throw rejectWithValue(err.message);
    }
    return data.foods;
  }
);

const listFrom = (payload: any, keys: string[]) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (!payload || typeof payload !== 'object') {
    return [];
  }
  const list = keys.map((key) => payload[key]).find((value) => Array.isArray(value));
  return list ?? [];
};

const productFrom = (payload: any) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return {};
  }
  if (
    payload.name ||
    payload.title ||
    payload.category ||
    payload.categoryId ||
    payload.image
  ) {
    return payload;
  }
  return payload.food ?? payload.data ?? payload;
};

export const fetchStatic = createAsyncThunk('fetchStatic', async () => {
  const [measures] = await tryCatch<any>(api.getMeasures());
  const [ages] = await tryCatch<any>(api.getAges());
  const [categories] = await tryCatch<any>(api.getCategories());

  return {
    measures: listFrom(measures, ['measures', 'data']),
    ages: listFrom(ages, ['ages', 'data']),
    categories: listFrom(categories, ['categories', 'foodCategories', 'data']),
  };
});

export const deleteSingle = createAsyncThunk(
  'deleteSingle',
  async (id: string) => {
    const [] = await tryCatch<any>(api.delete(id));

    return { id };
  }
);

export const fetchForEdit = createAsyncThunk(
  'fetchForEdit',
  async (id: string, { rejectWithValue, dispatch }) => {
    const [recipe, recipeErr] = await tryCatch<IFoods>(api.getSingle(id));
    if (recipeErr || !recipe) {
      dispatch(
        setNotification({
          value: 'Ошибка при получении продукта',
          variant: 'error',
        })
      );
      throw rejectWithValue(recipeErr?.message ?? 'not found');
    }

    const food = productFrom(recipe);
    const [rules] = await tryCatch<any>(api.getRules(+id));
    const [recommendations] = await tryCatch<any>(
      api.getRecommendations(+id)
    );
    const rulesList = listFrom(rules, ['servingRules', 'rules', 'data']);
    const recommendationsList = listFrom(recommendations, [
      'recommendations',
      'data',
    ]);

    return {
      foods: food,
      recommendations: recommendationsList.length
        ? recommendationsList
        : listFrom(food, ['recommendations']),
      rules: rulesList.length
        ? rulesList
        : listFrom(food, ['servingRules', 'rules']),
    };
  }
);

export const updateOrCreate = createAsyncThunk(
  'updateOrCreate',
  async (
    { isEditing, id }: { isEditing?: boolean; id?: string },
    { rejectWithValue, dispatch, getState }
  ) => {
    const state = getState() as RootState;
    const {
      foods: {
        form: { main, recs, rules },
      },
    } = state;
    const { form } = state.foods;

    const imageFiles = ((main.image ?? []) as (File | string)[]).filter(
      (file) => typeof file === 'string' || file instanceof File
    );
    let img: string[] = [];
    try {
      img = imageFiles.length
        ? await uploadImg(imageFiles as File[] | string[])
        : [];
    } catch (error) {
      dispatch(
        setNotification({
          value: 'Ошибка при загрузке изображения',
          variant: 'error',
        })
      );
      throw rejectWithValue(
        (error as AxiosError)?.message ?? 'upload'
      );
    }

    if (
      !main.category?.id ||
      !main.color?.id ||
      !main.measure?.id ||
      !main.age?.id
    ) {
      dispatch(
        setNotification({
          value: 'Заполните обязательные поля',
          variant: 'error',
        })
      );
      throw rejectWithValue('validation');
    }

    const data = {
      name: main.title,
      description: main.description,
      image: img?.[0],
      categoryId: main.category.id,
      possibleAllergy: !!main.isMarked,
      backgroundColor: main.color.id,
      measureId: main.measure.id,
      fromWhatAgeId: main.age.id,
    };
    const handleErr = (err: AxiosError, text?: string): never => {
      console.log(err);
      dispatch(
        setNotification({
          value: `${text ?? 'Упс... Что-то пошло не так'}`,
          variant: 'error',
        })
      );
      throw rejectWithValue(err.response?.status ?? err.message);
    };
    const [foods, err] = await tryCatch<IFoods>(
      isEditing ? api.update(id, data) : api.create(data)
    );

    if (err) {
      handleErr(
        err,
        `Ошибка при ${isEditing ? 'редактировании' : 'создании'} продукта [${
          err.response?.status ?? 'сеть'
        }]`
      );
    }

    if (!isEditing) {
      return foods;
    }
    const productId = +id;

    {
      const mapRules = async (items: IRule[]): Promise<IApiRule[]> => {
        return Promise.all(
          items.map(async (rule) => {
            const files = ((rule.img ?? []) as (File | string)[]).filter(
              (file) => typeof file === 'string' || file instanceof File
            );
            const image = files.length
              ? await uploadImg(files as File[] | string[])
              : [];
            return {
              image: image?.[0],
              name: rule.name,
              fromWhatAgeId: rule.ageId,
              id: rule.id,
              order: rule.order,
            };
          })
        );
      };
      const initRulesIds = form.initRules.map((elem) => elem.id);
      const allRules = [...rules]
        .filter((v) => (v.name ?? '').trim() !== '')
        .map((v, index) => ({
          ...v,
          order: index,
        }));

      const deletedRules = form.initRules
        .filter((v) => !rules.some((q) => q.id === v.id))
        .map((v) => v.id);

      if (deletedRules.length) {
        const [, deleteErr] = await tryCatch(
          api.deleteRules(deletedRules, productId)
        );
        if (deleteErr) {
          handleErr(
            deleteErr,
            `Ошибка при удалении правил [${deleteErr.response?.status ?? 'сеть'}]`
          );
        }
      }
      const rulesToAdd = getUniqueElemsFromFirstArray(allRules, initRulesIds);

      if (rulesToAdd?.length) {
        const newRules = await mapRules(rulesToAdd);
        const [, addErr] = await tryCatch(
          api.addRules(
            newRules.map(({ image, name, fromWhatAgeId, order }) => ({
              image,
              name,
              fromWhatAgeId,
              order,
            })),
            productId
          )
        );
        if (addErr) {
          handleErr(
            addErr,
            `Ошибка при добавлении правил [${addErr.response?.status ?? 'сеть'}]`
          );
        }
      }
      const rulesToEdit = getTouchedElemsFromFirstArray(
        allRules.map((elem) => ({
          value: elem,
        })),
        form.initRules,
        initRulesIds
      ).map((elem) => elem.value);
      if (rulesToEdit?.length) {
        const [, updateErr] = await tryCatch(
          api.updateRules(await mapRules(rulesToEdit), productId)
        );
        if (updateErr) {
          handleErr(
            updateErr,
            `Ошибка при изменении правил [${updateErr.response?.status ?? 'сеть'}]`
          );
        }
      }
    }

    {
      const initRecsIds = form.initRecommendations.map((elem) => elem.id);
      const allRecs = [...recs]
        .filter((v) => !!(v.text ?? '').trim())
        .map((v, index) => ({
          text: v.text,
          id: v.id,
          order: index,
        }));

      const deletedRecs = form.initRecommendations
        .filter((v) => !recs.some((q) => q.id === v.id))
        .map((v) => v.id);

      if (deletedRecs.length) {
        const [, deleteErr] = await tryCatch(
          api.deleteRecommendations(deletedRecs, productId)
        );
        if (deleteErr) {
          handleErr(
            deleteErr,
            `Ошибка при удалении рекомендаций [${deleteErr.response?.status ?? 'сеть'}]`
          );
        }
      }
      const recsToAdd = getUniqueElemsFromFirstArray(allRecs, initRecsIds);

      if (recsToAdd?.length) {
        const [, addErr] = await tryCatch(
          api.addRecommendations(recsToAdd, productId)
        );
        if (addErr) {
          handleErr(
            addErr,
            `Ошибка при добавлении рекоммендаций [${addErr.response?.status ?? 'сеть'}]`
          );
        }
      }
      const recsToEdit = getTouchedElemsFromFirstArray(
        allRecs.map((elem) => ({
          value: elem,
        })),
        form.initRecommendations,
        initRecsIds
      ).map((elem) => elem.value);

      if (recsToEdit?.length) {
        const [, updateErr] = await tryCatch(
          api.updateRecommendations(recsToEdit, productId)
        );
        if (updateErr) {
          handleErr(
            updateErr,
            `Ошибка при изменении рекомендаций [${updateErr.response?.status ?? 'сеть'}]`
          );
        }
      }
    }

    return foods;
  }
);
